import React, { ComponentProps, useEffect, useState } from 'react';
import { asView } from '../../../_lib/ViewFetcher';
import { HyperlinkTarget } from '../../../components/atoms/Hyperlink';
import { ContentList, ContentListItem } from '../../../_lib/components/ContentList';
import { ContentLink } from '../../../_lib/components/ContentLink';
import ContentMessage from '../../../_lib/components/ContentMessage/ContentMessage';
import ILink from '../../../types/ILink';
import { Option, Select } from '../../../_lib/Form2';
import { DateFormat, DateFormats } from '../../../_lib/components/DateFormat';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { isNullOrUndefined } from '../../../lib/typeGuards';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';

type RssFeed = {
  title: string;
  id: string;
  // entries are undefined if BE cant fetch the feed
  entries?: { link: ILink; date: string }[];
};

export type RssFeedProps = {
  feeds?: RssFeed[];
};

function RssFeedDefault({ feeds }: RssFeedProps) {
  const { t } = useSonTranslation('RssFeed');
  const controlId = useHtmlValidId();

  const [activeFeed, setActiveFeed] = useState<RssFeed | undefined>(feeds && feeds.length > 0 ? feeds[0] : undefined);

  useEffect(() => {
    if (feeds && feeds.length > 0) {
      setActiveFeed(feeds[0]);
    }
  }, [feeds]);

  const setActiveFeedByName = (id: string) => {
    const foundFeed = feeds?.find(feed => feed.id === id);
    if (foundFeed) {
      setActiveFeed(foundFeed);
    }
  };

  if (isNullOrUndefined(feeds) || feeds.length === 0) {
    return <ContentMessage text={t('defaultView.noFeedsConfigured')} />;
  }

  const getFeedItems = () => {
    if (!activeFeed || isNullOrUndefined(activeFeed.entries)) {
      return <ContentMessage text={t('defaultView.failedToFetchFeed')} />;
    }

    if (activeFeed.entries.length === 0) {
      return <ContentMessage text={t('defaultView.noMessagesInFeed')} />;
    }

    return (
      <ContentList ordered key={activeFeed.id}>
        {activeFeed.entries?.map((feedItem) => {
          return (
            <ContentListItem key={`${feedItem.date}-${feedItem.link.name}`}>
              {feedItem.link.url
                ? (
                    <ContentLink
                      to={feedItem.link.url}
                      target={HyperlinkTarget.BLANK}
                      routing={false}
                      external
                      newWindowIndicator
                    >
                      {feedItem.link.name}
                    </ContentLink>
                  )
                : (
                    <p className="rss-feed__content-list-item-title">{feedItem.link.name}</p>
                  )}
              <DateFormat className="rss-feed__date" date={feedItem.date} format={DateFormats.DATE_TIME} />
            </ContentListItem>
          );
        })}
      </ContentList>
    );
  };

  return (
    <>
      <div className="rss-feed">
        <Select
          key={feeds.length} // key here so select resets when new feeds are added
          label={t('defaultView.selectedFeed')}
          labelHidden
          aria-controls={controlId}
          onChange={e => setActiveFeedByName(e.target.value)}
        >
          {feeds.map(feed => (
            <Option key={feed.id} value={feed.id}>
              {feed.title}
            </Option>
          ))}
        </Select>
      </div>
      {/* div with controlId must always be in DOM, because it's controlled via the <select>'s aria-controls */}
      <div id={controlId} className="rss-feed__list-container">
        {getFeedItems()}
      </div>
    </>
  );
}

export const RssFeedDefaultView = asView(RssFeedDefault);
export type RssFeedDefaultView = ComponentProps<typeof RssFeedDefaultView>;
